import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { MultiCheckDropdown } from "Components/MultiCheckDropdown";
import { ButtonLoader } from "Components/ButtonLoader";
import { authService } from "Service/Axios";
import { Validator } from "Utils/validator";
import { CREATE_ADMIN_ROLE_URL, UPDATE_ADMIN_ROLE_URL } from "Constant/api";
import { useAttributeList, useUserList } from "Constant/apiHooks";

export const CreateRole = ({ show, toggle, selectedData, fetchList }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    roleName: "",
    roleDescription: "",
    userIds: [],
    attributeIds: [],
  });
  const [errMsg, setErrMsg] = useState({
    roleName: "",
    roleDescription: "",
    userIds: "",
    attributeIds: "",
  });

  const { roleName, roleDescription, userIds, attributeIds } = formData;

  const { userList, loading: userLoading } = useUserList();
  const { attributeList, loading: attributeLoading } = useAttributeList();

  // Listing
  const modifiedUserList = useMemo(() => {
    return userList?.length > 0
      ? userList.map((res) => ({
          label: `${res.firstName} ${res.lastName}`,
          value: res.userId,
        }))
      : [];
  }, [userList]);

  const modifiedAttributeList = useMemo(() => {
    return attributeList?.length > 0
      ? attributeList.map((res) => ({
          label: `${res.name} (${res.project?.name})`,
          value: res.gid,
        }))
      : [];
  }, [attributeList]);

  // On Change
  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (e.target.name === "roleName") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          roleName: t("common.messages.error.adminSettings.roleName.blank"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          roleName: "",
        }));
      }
    }
    if (e.target.name === "roleDescription") {
      if (!Validator.text(e.target.value)) {
        setErrMsg((prev) => ({
          ...prev,
          roleDescription: t(
            "common.messages.error.adminSettings.roleDescription.blank"
          ),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          roleDescription: "",
        }));
      }
    }
  };

  const handleChangeMultiple = (selected, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selected,
    }));
    if (name === "userIds") {
      if (!Validator.hasMultiple(selected)) {
        setErrMsg((prev) => ({
          ...prev,
          userIds: t("common.messages.error.adminSettings.userIds.blank"),
        }));
      } else {
        setErrMsg((prev) => ({
          ...prev,
          userIds: "",
        }));
      }
    }
  };

  useEffect(() => {
    if (selectedData) {
      setFormData({
        roleName: selectedData.name,
        roleDescription: selectedData.description,
        attributeIds: selectedData.attributes?.map((attribute) => ({
          label: attribute.name,
          value: attribute.id,
        })),
        userIds: selectedData.users?.map((user) => ({
          label: `${user.firstName} ${user.lastName}`,
          value: user.userId,
        })),
      });
    }
  }, [selectedData]);

  const getValidation = () => {
    return new Promise((resolve) => {
      if (
        Validator.text(roleName) &&
        Validator.text(roleDescription) &&
        Validator.hasMultiple(userIds)
      ) {
        resolve(true);
      } else {
        if (!Validator.text(roleName)) {
          setErrMsg((prev) => ({
            ...prev,
            roleName: t("common.messages.error.adminSettings.roleName.blank"),
          }));
        }

        if (!Validator.text(roleDescription)) {
          setErrMsg((prev) => ({
            ...prev,
            roleDescription: t(
              "common.messages.error.adminSettings.roleDescription.blank"
            ),
          }));
        }

        if (!Validator.hasMultiple(userIds)) {
          setErrMsg((prev) => ({
            ...prev,
            userIds: t("common.messages.error.adminSettings.userIds.blank"),
          }));
        }
        resolve(false);
      }
    });
  };

  // On Submit
  const onSaveStage = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      setLoading(true);
      try {
        const payload = {
          name: roleName,
          users: userIds.map((user) => user.value),
          attributes: attributeIds.map((attribute) => attribute.value),
          description: roleDescription,
        };
        if (global === "project") {
          payload.projects = userIds?.map((option) => option.value);
        }
        if (selectedData) {
          payload.gid = selectedData.gid;
          const response = await authService(
            UPDATE_ADMIN_ROLE_URL,
            "POST",
            payload
          );
          if (response.success) {
            toast.success(t("common.messages.notification.success.updateRole"));
          } else {
            toast.error(response.message);
          }
        } else {
          const response = await authService(
            CREATE_ADMIN_ROLE_URL,
            "POST",
            payload
          );
          if (response.success) {
            toast.success(t("common.messages.notification.success.createRole"));
          } else {
            toast.error(response.message);
          }
        }
        setLoading(false);
        fetchList(userIds);
        toggle();
      } catch (error) {
        setLoading(false);
        if (error.response && error.response.data) {
          toast.error(error.response.data.message);
        }
      }
    }
  };

  return (
    <Modal
      show={show}
      onHide={toggle}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className="justify-content-center">
        <Modal.Title>{t("auth.adminSettings.createRole")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={`mb-4 ${errMsg.roleName ? "error" : ""}`}>
          <label htmlFor="roleName" className="form-label">
            {t("common.label.name")}
          </label>
          <input
            type="text"
            className="form-control"
            placeholder={t("common.placeHolder.name")}
            id="roleName"
            name="roleName"
            value={roleName}
            onChange={handleChange}
          />
          {errMsg.roleName && <p>{errMsg.roleName}</p>}
        </div>
        <div className={`mb-4 ${errMsg.roleDescription ? "error" : ""}`}>
          <label htmlFor="roleDescription" className="form-label">
            {t("common.label.description")}
          </label>
          <input
            type="text"
            className="form-control"
            placeholder={t("common.placeHolder.description")}
            id="roleDescription"
            name="roleDescription"
            value={roleDescription}
            onChange={handleChange}
          />
          {errMsg.roleDescription && <p>{errMsg.roleDescription}</p>}
        </div>
        <div className={`mb-4 ${errMsg.userIds ? "error" : ""}`}>
          <label htmlFor="userIds" className="form-label">
            {t("common.label.users")}
          </label>
          <MultiCheckDropdown
            placeholder={t("common.placeHolder.users")}
            options={modifiedUserList}
            value={userIds}
            onChange={(selected) => handleChangeMultiple(selected, "userIds")}
            disabled={userLoading}
          />
          {errMsg.userIds && <p>{errMsg.userIds}</p>}
        </div>

        <div className={`mb-4 ${errMsg.sequenceNo ? "error" : ""}`}>
          <label htmlFor="attributeIds" className="form-label">
            {t("common.label.attributes")}
          </label>
          <MultiCheckDropdown
            placeholder={t("common.placeHolder.attributes")}
            options={modifiedAttributeList}
            value={attributeIds}
            onChange={(selected) =>
              handleChangeMultiple(selected, "attributeIds")
            }
            disabled={attributeLoading}
          />
          {errMsg.attributeIds && <p>{errMsg.attributeIds}</p>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={toggle}>
          {t("common.button.close")}
        </Button>
        {!loading ? (
          <Button variant="primary" onClick={onSaveStage}>
            {t("common.button.save")}
          </Button>
        ) : (
          <ButtonLoader className="btn-loading-sm" />
        )}
      </Modal.Footer>
    </Modal>
  );
};
